// src/components/tickets/TicketFilterBar.tsx
//
// The five combinable filters (EXPERIENCE.md → Filter bar): question type,
// brand, route label, age, and (Story 6.3) reference code. The first four are
// labelled <select>s; the reference filter is an ordinary visible text input —
// EXPERIENCE.md bans command palettes, and the click-first audience should see
// the control, not discover it. Changing any one calls onChange with the next
// filter set (the parent persists to sessionStorage and resets to page 1). A
// "Clear filters" affordance appears only when at least one filter is active.
// All controls carry a real <label> (WCAG 2.2 AA).
//
// The reference input matters most on Closed: AUTO tickets live there, and an
// AUTO code is exactly what a rep quotes when they call back.

import { useState } from 'react'
import { QUESTION_TYPES } from '../../utils/questionTypes'
import { questionTypeLabel } from '../../utils/ticketDisplay'
import { hasActiveFilters, EMPTY_TICKET_FILTERS } from '../../utils/viewFilters'
import type {
  YakTicketFilters,
  YakTicketAgeBucket,
  YakTicketRoute,
  YakQuestionType,
} from '../../types/yak'

interface TicketFilterBarProps {
  filters: YakTicketFilters
  brandOptions: string[]
  onChange: (next: YakTicketFilters) => void
}

const AGE_OPTIONS: { value: YakTicketAgeBucket; label: string }[] = [
  { value: '1d', label: 'Older than 1 day' },
  { value: '3d', label: 'Older than 3 days' },
  { value: '7d', label: 'Older than 7 days' },
]

const selectClass =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
const labelClass = 'block text-xs font-medium text-gray-500 mb-1'

export function TicketFilterBar({
  filters,
  brandOptions,
  onChange,
}: TicketFilterBarProps) {
  const active = hasActiveFilters(filters)

  // The reference box is the one control that is NOT applied per keystroke: the
  // filter is an EXACT match, so every intermediate prefix would empty the list
  // and fire a query for a code nobody typed yet. It commits on Enter or blur.
  // The committed filter stays the source of truth — "Clear filters" and a
  // restored per-view filter set both reset the box through this effect.
  const [referenceDraft, setReferenceDraft] = useState(filters.reference ?? '')
  const [committed, setCommitted] = useState(filters.reference)
  if (committed !== filters.reference) {
    // React's "adjust state when a prop changes" pattern (NOT an effect —
    // an effect here would cascade a second render on every filter change).
    setCommitted(filters.reference)
    setReferenceDraft(filters.reference ?? '')
  }

  const commitReference = () => {
    const next = referenceDraft.trim() || null
    if (next !== filters.reference) onChange({ ...filters, reference: next })
  }

  return (
    <div className="card px-4 py-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <label htmlFor="filter-question-type" className={labelClass}>
            Question type
          </label>
          <select
            id="filter-question-type"
            className={selectClass}
            value={filters.questionType ?? ''}
            onChange={e =>
              onChange({
                ...filters,
                questionType: (e.target.value ||
                  null) as YakQuestionType | null,
              })
            }
          >
            <option value="">All types</option>
            {QUESTION_TYPES.map(type => (
              <option key={type} value={type}>
                {questionTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="filter-brand" className={labelClass}>
            Brand
          </label>
          <select
            id="filter-brand"
            className={selectClass}
            value={filters.brand ?? ''}
            onChange={e =>
              onChange({ ...filters, brand: e.target.value || null })
            }
          >
            <option value="">All brands</option>
            {brandOptions.map(brand => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="filter-route" className={labelClass}>
            Route
          </label>
          <select
            id="filter-route"
            className={selectClass}
            value={filters.routeLabel ?? ''}
            onChange={e =>
              onChange({
                ...filters,
                routeLabel: (e.target.value || null) as YakTicketRoute | null,
              })
            }
          >
            <option value="">All routes</option>
            <option value="auto">AUTO</option>
            <option value="review">REVIEW</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-age" className={labelClass}>
            Age
          </label>
          <select
            id="filter-age"
            className={selectClass}
            value={filters.age ?? ''}
            onChange={e =>
              onChange({
                ...filters,
                age: (e.target.value || null) as YakTicketAgeBucket | null,
              })
            }
          >
            <option value="">Any age</option>
            {AGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="filter-reference" className={labelClass}>
            Reference
          </label>
          <input
            id="filter-reference"
            type="text"
            className={selectClass}
            placeholder="e.g. YAK-20260809-001"
            aria-describedby="filter-reference-hint"
            value={referenceDraft}
            onChange={e => setReferenceDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commitReference()
            }}
            // Story 6.3 review: NO onBlur commit. The deferred-commit design
            // exists precisely so an intermediate prefix never fires a query for
            // a code nobody finished typing — and onBlur did exactly that, while
            // the hint below promised Enter. Worse, browsers fire blur before
            // click, so clicking "Clear filters" with a partial code ran
            // blur (commit + query + reset to page 1) then click (clear + query
            // again): two round trips and a flash of an empty list to clear.
          />
          <p id="filter-reference-hint" className="mt-1 text-xs text-gray-500">
            Press Enter to search
          </p>
        </div>
      </div>

      {active && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => onChange({ ...EMPTY_TICKET_FILTERS })}
            className="text-sm text-brand-600 hover:text-brand-700 hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  )
}
